import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import { List } from 'react-window';
import { InfiniteLoader } from 'react-window-infinite-loader';
import FormsListItem from '../FormsListItem';

import styles from './styles';
const useStyles = createUseStyles(styles, 'FormsList');

function FormsList({
    items,
    hasNextPage,
    isLoading,
    getFormsList,
    openDetail,
    editForm,
    deleteForm,
    height,
    ...props
}) {
    const classes = useStyles();
    const itemCount = useMemo(() => {
        return hasNextPage ? items.length + 1 : items.length;
    }, [items.length, hasNextPage]);

    const loadMoreItems = useMemo(() => {
        return isLoading ? () => {} : getFormsList;
    }, [getFormsList, isLoading]);

    const isItemLoaded = useCallback(
        (index) => {
            return !hasNextPage || index < items.length;
        },
        [items.length, hasNextPage],
    );

    const Row = useCallback(
        ({ index, style }) => {
            const item = items[index];
            if (!item || !item.list) return null;
            return (
                <FormsListItem
                    id={item.id}
                    idFormType={item.formTemplateId}
                    pos11={item.list.pos11}
                    pos12={item.list.pos12}
                    pos13={item.list.pos13}
                    pos21={item.list.pos21}
                    pos22={item.list.pos22}
                    pos23={item.list.pos23}
                    readOnly={item.endState}
                    openDetail={openDetail}
                    editForm={editForm}
                    deleteForm={deleteForm}
                    style={style}
                />
            );
        },
        [items, openDetail, editForm, deleteForm],
    );

    if (!items) {
        return null;
    }

    return (
        <div className={classes.root}>
            <InfiniteLoader
                isRowLoaded={isItemLoaded}
                rowCount={itemCount}
                loadMoreRows={loadMoreItems}
            >
                {({ onRowsRendered }) => (
                    <List
                        className={classes.list}
                        style={{ height }}
                        rowCount={itemCount}
                        rowHeight={96}
                        onRowsRendered={onRowsRendered}
                        rowProps={{}}
                        rowComponent={Row}
                    />
                )}
            </InfiniteLoader>
        </div>
    );
}

FormsList.propTypes = {
    items: PropTypes.array,
    hasNextPage: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool,
    getFormsList: PropTypes.func.isRequired,
    openDetail: PropTypes.func.isRequired,
    editForm: PropTypes.func.isRequired,
    deleteForm: PropTypes.func.isRequired,
    height: PropTypes.number.isRequired,
};

export default memo(FormsList);
